import { useParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid-pro';
import SharedToolbar from '../../../../../shared/shared-toolbar-latest';
import SharedGridStateIconButton from '../../../../../shared/shared-grid-state-icon-button-latest';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import type { GPanelContactsLabelsLogs as GPanelContactLog } from '../../shared/types.ts';
import { useGPanelLabelsLogsQuery } from '../../gpanel-contacts-queries.ts';
import { useMemo, useState } from 'react';
import { useStore } from '../../../../state/zustand-store.ts';
import { Messages } from '../../../../../utils/message-constants.ts';
import { useGpanelContactsPermissions } from '../../shared/hooks/use-gpanel-contacts-permissions.tsx';
import TooltipWithPermission from '../../shared/components/tooltip-with-permission.tsx';
import {
  GPanelContactsPermissions,
  hasPermission,
} from '../../../../../utils/roles/client-roles.ts';
import ChangeLogDialog from '../../../../../shared/change-log-dialog.tsx';
import { useAnalytics } from '../../../../../shared/analytics/create-events.ts';
import { useMutation } from '@tanstack/react-query';
import { exportLabelLogs } from '../../gpanel-contacts-api.ts';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { useUserSettings } from '../../../../../utils/use-user-settings.tsx';
import SharedBackButton from '../../shared/components/shared-back-button.tsx';
import SharedDataGridClient from '../../../../../shared/shared-data-grid-client';
import SharedIconButton from '../../../../../shared/buttons/shared-icon-button.tsx';

import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';

const formatUsDateTime = (value?: string | number | Date): string => {
  if (value == null) return '';

  const date =
    value instanceof Date
      ? value
      : new Date(typeof value === 'string' ? Number(value) : value);
  return (
    date.toLocaleDateString('en-US') +
    ' ' +
    date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
  );
};

type Row = GPanelContactLog & { id: string };

const columns: GridColDef<GPanelContactLog & { id: string }>[] = [
  {
    field: 'createdDate',
    headerName: 'Date',
    flex: 1,
    sortComparator: (v1, v2) => Number(v1) - Number(v2),
  },
  { field: 'performingUser', headerName: 'Performed User', flex: 1 },
  { field: 'action', headerName: 'Action', flex: 2 },
  {
    field: 'changeLogReason',
    headerName: 'Change Log Reason',
    flex: 3,
  },
];

interface LogsResponseWithResults {
  results: unknown;
}

const isLogsArray = (x: unknown): x is GPanelContactLog[] => Array.isArray(x);

const isLogsResponseWithResults = (x: unknown): x is LogsResponseWithResults =>
  typeof x === 'object' && x !== null && 'results' in x;

export default function GPanelContactsLabelsLogs() {
  const { labelId = '' } = useParams<{ labelId: string }>();
  const { changeLogRoles } = useUserSettings();
  const { PERMISSIONS } = useGpanelContactsPermissions();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { createEvent } = useAnalytics();

  const query = useGPanelLabelsLogsQuery(Number(labelId));
  const setSnackbar = useStore((state) => state.setSnackbar);
  const { mutate: exportLogs, isPending } = useMutation({
    mutationFn: (labelId: number) => exportLabelLogs(labelId, changeLogMessage),
    onSuccess: (spreadsheetId) => {
      window.open(
        `https://docs.google.com/spreadsheets/d/${spreadsheetId}`,
        '_blank'
      );

      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Exported('label logs'),
      });
      setChangeLogMessage('');
    },
    onError: () =>
      setSnackbar({
        open: true,
        severity: 'error',
        message: Messages.Errors.Network.Exporting('label logs'),
      }),
    onSettled: () => {
      setOpenChangeLogDialog(false);
      createEvent(analytics.directory.gpanel_contacts.export_label_logs, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.gpanelContacts,
      });
    },
  });

  const rows = useMemo<Row[]>(() => {
    let logs: GPanelContactLog[] = [];

    if (isLogsArray(query.data)) {
      logs = query.data;
    } else if (
      isLogsResponseWithResults(query.data) &&
      isLogsArray(query.data.results)
    ) {
      logs = query.data.results;
    }

    return logs.map((log, idx) => ({
      ...log,
      id: `${idx}-${log.createdDate}`,
      createdDate: formatUsDateTime(log.createdDate),
    }));
  }, [query.data]);

  const handleDownload = () => {
    if (
      hasPermission(
        changeLogRoles,
        GPanelContactsPermissions.ExportLabelLogs.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      exportLogs(Number(labelId));
    }
  };

  const handleRefresh = () => {
    query.refetch().then((r) => r);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        p: 0,
        overflow: 'hidden',
      }}
    >
      <Stack width={1} sx={{ flex: 1, height: 1, overflow: 'hidden', mt: 0.5 }}>
        <SharedBackButton />

        <Stack width={1} sx={{ flex: 1, height: 1, overflow: 'hidden' }}>
          <SharedDataGridClient
            slots={{
              toolbar: () => (
                <SharedToolbar>
                  <TooltipWithPermission
                    title={GPanelContactsPermissions.ExportLabelLogs.name}
                    hasPermission={PERMISSIONS.exportLabelLogs}
                    permissionName={
                      GPanelContactsPermissions.ExportLabelLogs.name
                    }
                    renderElement={(hasPermission) => (
                      <SharedIconButton
                        variant="export"
                        onClick={handleDownload}
                        disabled={isPending || !hasPermission}
                        title="Download"
                        isLoading={isPending}
                        disableTooltip
                      />
                    )}
                  />
                  <SharedIconButton
                    variant="refresh"
                    onClick={handleRefresh}
                    title="Refresh"
                    tooltipLabel="Refresh"
                  />
                  <SharedGridStateIconButton
                    gridStateKey="gpanelContactsGridStates"
                    module={ModuleList.modules.directory.name}
                    section={
                      ModuleList.modules.directory.sections.gpanelContacts
                    }
                  />
                </SharedToolbar>
              ),
            }}
            columns={columns}
            rows={rows}
            loading={query.isFetching}
            disableRowSelectionOnClick
            pagination
            pageSizeOptions={[25, 50, 100]}
            defaultGridState={{
              pagination: { paginationModel: { pageSize: 25, page: 0 } },
            }}
            sx={{
              flex: 1,
              '& .MuiDataGrid-row:hover': {
                cursor: 'default',
              },
              '&.MuiDataGrid-root': { border: 'none' },
              '& .MuiDataGrid-columnHeaders': {
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
              },
            }}
          />
        </Stack>
      </Stack>
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => exportLogs(Number(labelId))}
        mutateLoading={isPending}
      />
    </Box>
  );
}
